import { Button, Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import React, { useState, useEffect, useCallback } from 'react';

import { useAuth } from '@/shared/contexts/AuthContext';
import { useUserApi } from '@/shared/services/userApi';
import FeatureIconCircle from '@/components/common/FeatureIconCircle';
import OnboardingTour from '@/components/common/OnboardingTour';
import SEO from '@/components/common/SEO';
import babyBiliIcon from '@/assets/img/icons/svg/babyBili.svg';
import globeAmericasIcon from '@/assets/img/icons/svg/globeAmericas.svg';
import peopleIcon from '@/assets/img/icons/svg/peopleIcon.svg';
import searchIcon from '@/assets/img/icons/svg/search.svg';

import './Dashboard.css';

export default function Dashboard() {
    const { user, loading, updateTourState, isAdmin } = useAuth();
    const [profile, setProfile] = useState(null);
    const [showTour, setShowTour] = useState(false);
    const [tourInitialStep, setTourInitialStep] = useState(0);
    const { fetchProfile, resetTour } = useUserApi();

    // Fetch user profile data from MongoDB
    useEffect(() => {
        const fetchUserProfile = async () => {
            if (loading || !user) {
                return;
            }

            try {
                const profileData = await fetchProfile();
                setProfile(profileData);

                // Show onboarding tour for users who haven't completed it
                if (profileData && !profileData.hasSeenTour) {
                    setTourInitialStep(profileData.tourStep || 0);
                    setShowTour(true);
                }
            } catch {
                // Continue with Firebase data as fallback
            }
        };

        fetchUserProfile();
    }, [user, loading, fetchProfile]);

    const handleStartTour = useCallback(
        (step = 0) => {
            setTourInitialStep(step);
            setShowTour(true);
            // Reset backend tour state so ContinueTourBanner shows on navigated pages
            resetTour()
                .then(() => updateTourState(false, 0))
                .catch(() => {});
        },
        [resetTour, updateTourState]
    );

    const handleTourComplete = useCallback(() => {
        setShowTour(false);
        setProfile((prev) => (prev ? { ...prev, hasSeenTour: true } : prev));
        // Update global auth context so ContinueTourBanner hides everywhere.
        // Uses direct state setter instead of refreshProfileStatus() to avoid
        // triggering profileLoading, which unmounts Dashboard via ProtectedRoutes.
        updateTourState(true, 0);
    }, [updateTourState]);

    // Called when user clicks a feature link mid-tour — dismiss modal but keep
    // tour incomplete so ContinueTourBanner shows on the target page.
    const handleTourPause = useCallback(
        (nextStep) => {
            setShowTour(false);
            updateTourState(false, nextStep);
        },
        [updateTourState]
    );

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good morning';
        if (hour < 18) return 'Good afternoon';
        return 'Good evening';
    };

    const features = [
        {
            title: 'Bili Chat',
            description:
                'Ask Bili about sustainability in Colorado \u2014 local resources, environmental data, and what\u2019s happening in your community.',
            icon: babyBiliIcon,
            iconFullColor: true,
            iconVariant: 'primary',
            path: '/bili-chat',
            cta: 'Explore',
        },
        {
            title: 'Spotlights',
            description:
                'Deep dives into Colorado sustainability topics like wildfires, water, and data centers \u2014 with real data, analysis, and community discussion.',
            icon: globeAmericasIcon,
            iconVariant: 'danger',
            path: '/spotlights',
            cta: 'Explore',
        },
        {
            title: 'Communities of Interest',
            description:
                'Join topic-based groups to discuss sustainability, share resources, and connect with others across Colorado.',
            icon: peopleIcon,
            iconVariant: 'info',
            path: '/communities',
            cta: 'Explore',
        },
        {
            title: 'Data Discovery',
            description:
                'Browse curated environmental datasets, data products, and organizations powering sustainability research.',
            icon: searchIcon,
            iconVariant: 'secondary',
            path: '/data',
            cta: 'Explore',
        },
        {
            title: 'Advanced Query',
            description:
                'Build custom queries to search sustainability data with filters, keywords, and expert tools.',
            icon: null,
            bootstrapIcon: 'bi-sliders',
            iconVariant: 'warning',
            path: '/advanced-query',
            cta: 'Explore',
        },
    ];

    return (
        <section id="dashboard" className="page-container">
            <SEO title="Dashboard" />
            <OnboardingTour
                show={showTour}
                onComplete={handleTourComplete}
                onPause={handleTourPause}
                initialStep={tourInitialStep}
            />
            <Container className="py-5">
                {/* Welcome Header */}
                <Row className="mb-5">
                    <Col lg={8} className="mx-auto text-center">
                        <h1 className="display-4 fw-bold text-primary mb-3">
                            {getGreeting()}
                            {profile?.name?.first && profile?.name?.last
                                ? `, ${profile.name.first} ${profile.name.last.charAt(0)}.`
                                : ''}
                            !
                        </h1>
                        <p className="lead mb-3">
                            Welcome to your sustainability hub. Choose how
                            you&apos;d like to explore and engage with
                            environmental topics today.
                        </p>
                        <Button
                            variant="primary"
                            className="rounded-pill px-4"
                            onClick={() => handleStartTour(0)}
                        >
                            Take the Platform Tour
                        </Button>
                    </Col>
                </Row>

                {/* Admin Section */}
                {isAdmin && (
                    <Row className="mb-4">
                        <Col lg={8} className="mx-auto">
                            <Card className="border-0 shadow-sm border-start border-danger border-4">
                                <Card.Body className="p-4">
                                    <div className="d-flex align-items-center mb-3">
                                        <span
                                            className="fs-4 me-2"
                                            role="img"
                                            aria-label="Admin"
                                        >
                                            &#x2699;&#xFE0F;
                                        </span>
                                        <h5 className="fw-bold text-danger mb-0">
                                            Admin Tools
                                        </h5>
                                    </div>
                                    <p className="text-muted mb-3">
                                        Manage data sources, products, and
                                        platform health.
                                    </p>
                                    <div className="d-flex flex-wrap gap-2">
                                        <Button
                                            as={Link}
                                            to="/admin/data-sources"
                                            variant="outline-danger"
                                            size="sm"
                                            className="rounded-pill"
                                        >
                                            Data Admin Hub
                                        </Button>
                                        <Button
                                            as={Link}
                                            to="/data?tab=sources"
                                            variant="outline-info"
                                            size="sm"
                                            className="rounded-pill"
                                        >
                                            Browse Sources
                                        </Button>
                                        <Button
                                            as={Link}
                                            to="/data?tab=products"
                                            variant="outline-info"
                                            size="sm"
                                            className="rounded-pill"
                                        >
                                            Browse Products
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Feature Cards */}
                <Row className="g-4 mb-5 justify-content-center">
                    {features.map((feature) => (
                        <Col key={feature.title} xs={12} sm={6} lg={4}>
                            <Card
                                as={Link}
                                to={feature.path}
                                className="feature-highlight-card h-100 border-0 shadow-sm rounded-4 text-decoration-none text-reset p-4"
                            >
                                <Card.Body className="d-flex flex-column align-items-center text-center p-0">
                                    <FeatureIconCircle
                                        icon={feature.icon}
                                        bootstrapIcon={feature.bootstrapIcon}
                                        iconVariant={feature.iconVariant}
                                        fullColor={feature.iconFullColor}
                                        size="md"
                                        className="mb-3"
                                    />
                                    <Card.Title
                                        as="h2"
                                        className="fs-5 fw-bold text-primary"
                                    >
                                        {feature.title}
                                    </Card.Title>
                                    <Card.Text className="text-muted flex-grow-1">
                                        {feature.description}
                                    </Card.Text>
                                    <Button
                                        variant="link"
                                        className="text-primary fw-semibold p-0 mt-2"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                    >
                                        {feature.cta} &rarr;
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>
    );
}
